//// Application Packages
import React, { useState, useEffect } from 'react'
import parse from 'html-react-parser'
import { useSelector, useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { object, string } from 'yup'
import { format } from 'date-fns'
import { translateMultiFormField } from '../../functions/field-functions'
import {
    subtractValuesAdditionalArrayForm,
    extractSubmissionForm,
} from '../../functions/form-functions'

//// Application Slices
import { selectSettings } from '../../reducers/settingSlice'
import {
    updateFormData,
    updateFormValidity,
    updateFormAuxiliary,
    deleteFormData,
    updateFormSystem,
    selectForms,
} from '../../reducers/formSlice'

//// Application Components
import ButtonComponent from '../../components/conditional-components/button-component'
import LoadingComponent from '../../components/simple-components/loading-component'
import NotificationComponent from '../../components/conditional-components/notification-component'
import AddFormComponent from '../../components/navigation-components/add-form-component'
import StateSelectorComponent from '../../components/conditional-components/state-selector-component'
import CountrySelectorComponent from '../../components/conditional-components/country-selector-component'
import AdditionalEducationHistoryComponent from '../../components/generic-components/additional-education-history-component'

//// Carbon Components
import { Form, TextInput, DatePicker, DatePickerInput } from '@carbon/react'

//// Carbon Icons

//// Static Constants

//////// Component
const EducationHistoryForm = ({ instance }) => {
    //////// Hooks
    const dispatch = useDispatch()
    const navigate = useNavigate()

    //////// State
    const [loading, setLoading] = useState(false)
    const [notification, setNotification] = useState({})

    //////// Slices
    const settingsData = useSelector(selectSettings)
    const formsData = useSelector(selectForms)

    //// Slice Data
    const digital_name = settingsData?.['form']?.['digital_name']
    const submit_route = settingsData?.['form']?.['submit_route']
    const application_specific_copy =
        settingsData?.['form']?.['application_specific_copy']
    const fields = settingsData?.['form']?.['fields']
    const max_form_iterations =
        settingsData?.['application']?.['max_form_iterations']

    //////// Search Params

    //////// Methods

    // Application Specific Copy
    const handleApplicationSpecificCopy = () => {
        if (application_specific_copy) {
            const copy = parse(`${application_specific_copy}`)
            if (copy) {
                return copy
            } else {
                return null
            }
        } else {
            return null
        }
    }

    // Generate Validation Schema
    const generateSchema = async (auxAddtForms) => {
        try {
            const dynamic = {}
            if (auxAddtForms?.length > 0) {
                dynamic.addtObjs = [
                    {
                        INST_NME: '',
                        SchlAddrOne: '',
                        SchlAddrTwo: '',
                        SchlAddrThree: '',
                        ZIP: '',
                        CITY: '',
                        STATE: '',
                        COUNTRY: '',
                        GRDT: '',
                        DGCR: '',
                        EDU_NME: '',
                    },
                ]
                dynamic.addtFilds = [
                    {
                        INST_NME: false,
                        INST_NMEErrMsg: '',
                        SchlAddrOne: false,
                        SchlAddrOneErrMsg: '',
                        SchlAddrTwo: false,
                        SchlAddrTwoErrMsg: '',
                        SchlAddrThree: false,
                        SchlAddrThreeErrMsg: '',
                        ZIP: false,
                        ZIPErrMsg: '',
                        CITY: false,
                        CITYErrMsg: '',
                        STATE: false,
                        STATEErrMsg: '',
                        COUNTRY: false,
                        COUNTRYErrMsg: '',
                        GRDT: false,
                        GRDTErrMsg: '',
                        DGCR: false,
                        DGCRErrMsg: '',
                        EDU_NME: false,
                        EDU_NMEErrMsg: '',
                    },
                ]
                dynamic.addtObjs[0] = {
                    ...dynamic.addtObjs[0],
                    INST_NME: '',
                    SchlAddrOne: '',
                    SchlAddrTwo: '',
                    SchlAddrThree: '',
                    ZIP: '',
                    CITY: '',
                    STATE: '',
                    COUNTRY: '',
                    GRDT: '',
                    DGCR: '',
                    EDU_NME: '',
                }
                dynamic.addtFilds[0] = {
                    ...dynamic.addtFilds[0],
                    INST_NME: '',
                    INST_NMEErrMsg: '',
                    SchlAddrOne: '',
                    SchlAddrOneErrMsg: '',
                    SchlAddrTwo: '',
                    SchlAddrTwoErrMsg: '',
                    SchlAddrThree: '',
                    SchlAddrThreeErrMsg: '',
                    ZIP: '',
                    ZIPErrMsg: '',
                    CITY: '',
                    CITYErrMsg: '',
                    STATE: '',
                    STATEErrMsg: '',
                    COUNTRY: '',
                    COUNTRYErrMsg: '',
                    GRDT: '',
                    GRDTErrMsg: '',
                    DGCR: '',
                    DGCRErrMsg: '',
                    EDU_NME: '',
                    EDU_NMEErrMsg: '',
                }
                await auxAddtForms.forEach(async (el) => {
                    dynamic.addtObjs.push({
                        [`INST_NME` + el]: '',
                        [`SchlAddrOne` + el]: '',
                        [`SchlAddrTwo` + el]: '',
                        [`SchlAddrThree` + el]: '',
                        [`ZIP` + el]: '',
                        [`CITY` + el]: '',
                        [`STATE` + el]: '',
                        [`COUNTRY` + el]: '',
                        [`GRDT` + el]: '',
                        [`DGCR` + el]: '',
                        [`EDU_NME` + el]: '',
                    })
                    dynamic.addtFilds.push({
                        [`INST_NME` + el]: '',
                        [`INST_NME` + el + 'ErrMsg']: false,
                        [`SchlAddrOne` + el]: '',
                        [`SchlAddrOne` + el + 'ErrMsg']: false,
                        [`SchlAddrTwo` + el]: '',
                        [`SchlAddrTwo` + el + 'ErrMsg']: false,
                        [`SchlAddrThree` + el]: '',
                        [`SchlAddrThree` + el + 'ErrMsg']: false,
                        [`ZIP` + el]: '',
                        [`ZIP` + el + 'ErrMsg']: false,
                        [`CITY` + el]: '',
                        [`CITY` + el + 'ErrMsg']: false,
                        [`STATE` + el]: '',
                        [`STATE` + el + 'ErrMsg']: false,
                        [`COUNTRY` + el]: '',
                        [`COUNTRY` + el + 'ErrMsg']: false,
                        [`GRDT` + el]: '',
                        [`GRDT` + el + 'ErrMsg']: false,
                        [`DGCR` + el]: '',
                        [`DGCR` + el + 'ErrMsg']: false,
                        [`EDU_NME` + el]: '',
                        [`EDU_NME` + el + 'ErrMsg']: false,
                    })
                })
                dynamic.addtObjsSchema = {}
                if (dynamic?.addtObjs?.length > 0) {
                    dynamic.addtObjs.forEach(async (el) => {
                        dynamic.addtObjsSchema = {
                            ...dynamic.addtObjsSchema,
                            ...el,
                        }
                    })
                }
                dynamic.addtFildsSchema = {}
                if (dynamic?.addtFilds?.length > 0) {
                    dynamic.addtFilds.forEach(async (el) => {
                        dynamic.addtFildsSchema = {
                            ...dynamic.addtFildsSchema,
                            ...el,
                        }
                    })
                }
                dynamic.addtObjsSchemaKeys = Object?.keys(
                    dynamic.addtObjsSchema
                )
                dynamic.addtFildsSchemaKeys = Object?.keys(
                    dynamic.addtFildsSchema
                )
                dynamic.addtSchema = {}
                if (dynamic?.addtObjsSchemaKeys?.length > 0) {
                    dynamic.addtObjsSchemaKeys.forEach(async (el) => {
                        if (el?.includes('INST_NME')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(120)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('SchlAddrOne')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(30)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('SchlAddrTwo')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().min(0).max(30),
                            }
                        } else if (el?.includes('SchlAddrThree')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().min(0).max(40),
                            }
                        } else if (el?.includes('ZIP')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(13)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('CITY')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(60)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('DGCR')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string()
                                    .min(0)
                                    .max(60)
                                    .required(
                                        'This field is required to be entered or selected.'
                                    ),
                            }
                        } else if (el?.includes('EDU_NME')) {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().min(0).max(60),
                            }
                        } else {
                            dynamic.addtSchema = {
                                ...dynamic.addtSchema,
                                [el]: string().required(
                                    'This field is required to be entered or selected.'
                                ),
                            }
                        }
                    })
                }
                dynamic.addtValid = {}
                if (dynamic?.addtFildsSchemaKeys?.length > 0) {
                    dynamic.addtFildsSchemaKeys.forEach(async (el) => {
                        if (el?.includes('ErrMsg')) {
                            dynamic.addtValid = {
                                ...dynamic.addtValid,
                                [el]: '',
                            }
                        } else {
                            dynamic.addtValid = {
                                ...dynamic.addtValid,
                                [el]: false,
                            }
                        }
                    })
                }
            }
            return dynamic
        } catch (error) {
            console.warn('Generate Schema Error:', error)
            return null
        }
    }

    // Validate Data Submission
    const validation = async (submit) => {
        // Validate Form Submission Data Against Schema
        try {
            // Schema Object
            const schema = {}
            if (formsData[digital_name].auxiliary.addtForms?.length === 0) {
                schema.yup = object({
                    INST_NME: string()
                        .min(0)
                        .max(120)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    SchlAddrOne: string()
                        .min(0)
                        .max(30)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    SchlAddrTwo: string().min(0).max(30),
                    SchlAddrThree: string().min(0).max(40),
                    ZIP: string()
                        .min(0)
                        .max(13)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    CITY: string()
                        .min(0)
                        .max(60)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    STATE: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    COUNTRY: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    GRDT: string().required(
                        'This field is required to be entered or selected.'
                    ),
                    DGCR: string()
                        .min(0)
                        .max(60)
                        .required(
                            'This field is required to be entered or selected.'
                        ),
                    EDU_NME: string().min(0).max(60),
                })
                schema.valid = {
                    INST_NME: false,
                    INST_NMEErrMsg: '',
                    SchlAddrOne: false,
                    SchlAddrOneErrMsg: '',
                    SchlAddrTwo: false,
                    SchlAddrTwoErrMsg: '',
                    SchlAddrThree: false,
                    SchlAddrThreeErrMsg: '',
                    ZIP: false,
                    ZIPErrMsg: '',
                    CITY: false,
                    CITYErrMsg: '',
                    STATE: false,
                    STATEErrMsg: '',
                    COUNTRY: false,
                    COUNTRYErrMsg: '',
                    GRDT: false,
                    GRDTErrMsg: '',
                    DGCR: false,
                    DGCRErrMsg: '',
                    EDU_NME: false,
                    EDU_NMEErrMsg: '',
                }
            } else if (
                formsData[digital_name].auxiliary.addtForms?.length > 0
            ) {
                const addtForms = formsData[digital_name].auxiliary.addtForms
                const { addtSchema, addtValid } = await generateSchema(
                    addtForms
                )
                schema.yup = object().shape({ ...addtSchema })
                schema.valid = addtValid
            }
            await schema.yup.validate(
                { ...submit },
                { strict: true, abortEarly: false }
            )
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: {},
                })
            )
            return true
        } catch (err) {
            const errors = {}
            if (err?.inner?.length > 0) {
                await err.inner.forEach(async (el) => {
                    if (el?.path) {
                        errors[el?.path] = true
                        if (el?.errors?.length > 0) {
                            const errName = el?.path + 'ErrMsg'
                            const errMsg = el?.errors?.join(' ')
                            const tran = translateMultiFormField(
                                fields,
                                max_form_iterations,
                                el?.path
                            )
                            if (tran) {
                                const tranErrMsg = await errMsg.replaceAll(
                                    el?.path,
                                    tran
                                )
                                errors[errName] = tranErrMsg
                            } else {
                                errors[errName] = el?.errors?.join(' ')
                            }
                        }
                    }
                })
            }
            //// Start Watson Assistant Journey

            //// End Watson Assistant Journey
            dispatch(
                updateFormValidity({
                    digital_name: digital_name,
                    errors: errors,
                })
            )
            return false
        }
    }

    // Re-Validate Data Submission
    const revalidation = async (event_name) => {
        try {
            // Update Validation State
            if (digital_name && event_name) {
                dispatch(
                    updateFormValidity({
                        digital_name: digital_name,
                        errors: {
                            [event_name]: false,
                            [event_name + 'ErrMsg']: '',
                        },
                    })
                )
            }
        } catch (error) {
            console.warn('Revalidation Error:', error)
            return null
        }
    }

    //////// Functional Methods

    // Handle Removal Of Additional Education History Component/s
    const handleSubtractAdditionalEducationHistoryComponent = async (e) => {
        try {
            if (e?.target?.name) {
                const addtForms = [
                    ...formsData[digital_name].auxiliary.addtForms,
                ]
                const subt = parseInt(e.target.name)
                const index = addtForms?.indexOf(subt)
                if (index > -1) {
                    addtForms.splice(index, 1)
                    dispatch(
                        updateFormAuxiliary({
                            digital_name: digital_name,
                            auxiliary: {
                                addtForms: addtForms,
                            },
                        })
                    )
                    const values = subtractValuesAdditionalArrayForm(
                        fields,
                        max_form_iterations,
                        formsData?.[digital_name]?.values,
                        index
                    )
                    if (values) {
                        dispatch(
                            deleteFormData({
                                digital_name: digital_name,
                            })
                        )
                        dispatch(
                            updateFormData({
                                digital_name: digital_name,
                                values: values,
                            })
                        )
                    }
                }
            }
        } catch (error) {
            console.warn(
                'Handle Subtract Additional Education History Component Error:',
                error
            )
            return null
        }
    }

    // Handle Data Of All Fields + Integer + DatePicker (ID)
    const handleData = (e, id) => {
        if (e?.target?.name && e?.target?.value) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [e.target.name]: e.target.value,
                    },
                })
            )
            revalidation(e.target.name)
        } else if (e?.target?.name) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: { [e.target.name]: '' },
                })
            )
            revalidation(e.target.name)
        } else if (e?.length > 0 && id) {
            dispatch(
                updateFormData({
                    digital_name: digital_name,
                    values: {
                        [id]: format(new Date(e?.[0]), 'MM/dd/yyyy'),
                    },
                })
            )
            revalidation(id)
        }
        //// Start Watson Assistant Journey
        //// End Watson Assistant Journey
    }

    // Handle Form Submission
    const handleSubmit = async () => {
        try {
            console.info('Education History: Submitted')

            setLoading(true)

            // Extract Form Data From Redux
            const submit = await extractSubmissionForm(digital_name, formsData)

            // Validate Form Submission Data
            const valid = await validation(submit)

            // Check If Form Data Is Valid
            if (valid) {
                console.info('Education History: Passed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: true },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: false,
                    kind: '',
                    title: '',
                    subtitle: '',
                })

                // Navigate To Next Form
                navigate(submit_route ? submit_route : '/')
            } else {
                console.info('Education History: Failed Valid')

                dispatch(
                    updateFormSystem({
                        digital_name: digital_name,
                        system: { valid: false },
                    })
                )
                setLoading(false)
                setNotification({
                    type: 'inline',
                    visible: true,
                    kind: 'error',
                    title: 'Form Errors:',
                    subtitle:
                        'There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.',
                })

                console.log(
                    'Education History Failed Valid: There are errors on the form (such as missing required info). Please scroll up and update the fields with red messages.'
                )
            }
        } catch (error) {
            console.warn('Handle Submit Error:', error)
            return null
        }
    }

    //// Functional Methods

    // Handle Keyboard Enter As Submit (Method)
    const handleKeyDown = async (event) => {
        try {
            if (event?.key === 'Enter') {
                await handleSubmit()
            }
        } catch (error) {
            console.warn('Handle Down Key Error:', error)
            return null
        }
    }

    //////// Memoization

    //////// Effects/Side Effects

    // Reload Watson Assistant Journey on Reload
    useEffect(() => {
        if (instance?.tours) {
            instance.tours.endTour()
            instance.tours.startTour(
                'Registered OR LPN Nursing by Exam Education Form Tour'
            )
        }
        return () => {}
    }, [instance])

    //////////////// Render & Return
    if (loading) {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <LoadingComponent />
                </Form>
            </>
        )
    } else {
        return (
            <>
                <Form
                    tabIndex={0}
                    onKeyDown={handleKeyDown}
                    onSubmit={handleSubmit}
                >
                    <TextInput
                        className="required"
                        labelText="School Name:"
                        id="INST_NME"
                        name="INST_NME"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.INST_NME}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.INST_NME}
                        invalidText={
                            formsData?.[digital_name]?.errors?.INST_NMEErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="School Address:"
                        id="SchlAddrOne"
                        name="SchlAddrOne"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.SchlAddrOne}
                        maxLength="30"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.SchlAddrOne}
                        invalidText={
                            formsData?.[digital_name]?.errors?.SchlAddrOneErrMsg
                        }
                    />

                    <TextInput
                        className=""
                        labelText=""
                        id="SchlAddrTwo"
                        name="SchlAddrTwo"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.SchlAddrTwo}
                        maxLength="30"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.SchlAddrTwo}
                        invalidText={
                            formsData?.[digital_name]?.errors?.SchlAddrTwoErrMsg
                        }
                    />

                    <TextInput
                        className=""
                        labelText=""
                        id="SchlAddrThree"
                        name="SchlAddrThree"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.SchlAddrThree}
                        maxLength="40"
                        type="text"
                        onChange={handleData}
                        invalid={
                            formsData?.[digital_name]?.errors?.SchlAddrThree
                        }
                        invalidText={
                            formsData?.[digital_name]?.errors
                                ?.SchlAddrThreeErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="ZIP Code:"
                        id="ZIP"
                        name="ZIP"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.ZIP}
                        maxLength="10"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.ZIP}
                        invalidText={
                            formsData?.[digital_name]?.errors?.ZIPErrMsg
                        }
                    />

                    <TextInput
                        className="required"
                        labelText="City:"
                        id="CITY"
                        name="CITY"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.CITY}
                        maxLength="60"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.CITY}
                        invalidText={
                            formsData?.[digital_name]?.errors?.CITYErrMsg
                        }
                    />

                    <CountrySelectorComponent
                        digital_name={digital_name}
                        value_key="COUNTRY"
                        state_value_key="STATE"
                        county_value_key=""
                    />

                    <StateSelectorComponent
                        digital_name={digital_name}
                        value_key="STATE"
                        country={formsData?.[digital_name]?.values?.COUNTRY}
                    />

                    <DatePicker
                        dateFormat="m/d/Y"
                        datePickerType="single"
                        onChange={(e) => handleData(e, 'GRDT')}
                        onClose={(e) => handleData(e, 'GRDT')}
                        value={formsData?.[digital_name]?.values?.GRDT}
                    >
                        <DatePickerInput
                            className="required"
                            labelText="Graduation Date:"
                            id="GRDT"
                            name="GRDT"
                            placeholder="MM/DD/YYYY"
                            type="text"
                            invalid={formsData?.[digital_name]?.errors?.GRDT}
                            invalidText={
                                formsData?.[digital_name]?.errors?.GRDTErrMsg
                            }
                        />
                    </DatePicker>

                    <TextInput
                        className="required"
                        labelText="Degree Awarded (Diploma, LPN, ADN, BSN):"
                        id="DGCR"
                        name="DGCR"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.DGCR}
                        maxLength="10"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.DGCR}
                        invalidText={
                            formsData?.[digital_name]?.errors?.DGCRErrMsg
                        }
                    />

                    <TextInput
                        className=""
                        labelText="What name(s) did you use when you received your nursing education?"
                        id="EDU_NME"
                        name="EDU_NME"
                        placeholder=""
                        value={formsData?.[digital_name]?.values?.EDU_NME}
                        maxLength="60"
                        type="text"
                        onChange={handleData}
                        invalid={formsData?.[digital_name]?.errors?.EDU_NME}
                        invalidText={
                            formsData?.[digital_name]?.errors?.EDU_NMEErrMsg
                        }
                    />

                    {formsData[digital_name].auxiliary.addtForms.map((el) => (
                        <AdditionalEducationHistoryComponent
                            key={el}
                            number={el}
                            handleData={handleData}
                            w
                            handleSubtractAdditionalEducationHistoryComponent={
                                handleSubtractAdditionalEducationHistoryComponent
                            }
                        />
                    ))}

                    <AddFormComponent />

                    {handleApplicationSpecificCopy()}

                    <NotificationComponent notification={notification} />

                    <div className="button-group">
                        <div className="button-group-item">
                            <ButtonComponent
                                click={handleSubmit}
                                title="Save Progress"
                            />
                        </div>
                    </div>
                </Form>
            </>
        )
    }
}

export default EducationHistoryForm
